/**
 * Výpočet turnajových tabulek (spec 17.5–17.8) — čistá logika bez UI, sdílená administrací
 * i veřejnou částí, aby obě zobrazovaly identické pořadí.
 *
 * Skóre se v DB drží po jednotlivých hrách (`tournament_score`, jeden řádek = jedna hra), takže
 * mřížka se skládá tady: `game_no` 1..N určuje sloupec, chybějící hra je `null` (nezadáno).
 */

/** Jeden řádek turnajové tabulky: účastník, jeho hry v pořadí `game_no`, a která z nich je bonifikační. */
data class TournamentResultRow(
    val entry: TournamentEntryDto,
    val scores: List<Int?>,
    val bonusGameNo: Int? = null,
) {
    val playedScores: List<Int> get() = scores.filterNotNull()
    val hasAnyScore: Boolean get() = playedScores.isNotEmpty()
    val total: Int get() = playedScores.sum()

    /** Skóre bonifikační hry (spec 17.6) — 0, pokud hráč žádnou neoznačil nebo ji nemá zadanou. */
    val bonusScore: Int get() = bonusGameNo?.let { scores.getOrNull(it - 1) } ?: 0

    /** Bonus = `bonus_pct` % skóre označené hry, včetně desetinných bodů. */
    fun bonusPoints(bonusPct: Int): Double = bonusScore * bonusPct / 100.0

    fun totalWithBonus(bonusPct: Int): Double = (total * 100 + bonusScore * bonusPct) / 100.0

    /** Hry seřazené sestupně — tie-break kvalifikace (nejvyšší hra, pak druhá nejvyšší, …). */
    val descendingScores: List<Int> get() = playedScores.sortedDescending()
}

/**
 * Poskládá mřížku pro danou fázi. Účastník bez jediného náhozu tu zůstává (má prázdný řádek) —
 * je přihlášený, jen ještě nehrál.
 */
fun buildTournamentRows(
    gamesPerPlayer: Int,
    entries: List<TournamentEntryDto>,
    scores: List<TournamentScoreDto>,
    phase: String = TOURNAMENT_PHASE_QUALIFICATION,
): List<TournamentResultRow> {
    val byEntry = scores.filter { it.phase == phase }.groupBy { it.entryId }
    return entries.map { entry ->
        val rows = byEntry[entry.id].orEmpty()
        val cells = MutableList<Int?>(gamesPerPlayer) { null }
        rows.forEach { score ->
            val idx = score.gameNo - 1
            if (idx in cells.indices) cells[idx] = score.score
        }
        TournamentResultRow(
            entry = entry,
            scores = cells,
            bonusGameNo = rows.firstOrNull { it.isBonus }?.gameNo,
        )
    }
}

/**
 * Řazení kvalifikace / jednoduchého turnaje (spec 17.5): součet sestupně, při shodě nejvyšší
 * jednotlivá hra, pak druhá nejvyšší atd. Když ani to nerozhodne, uplatní se ruční los
 * (`qual_rank`, který admin u dotčených hráčů vyplní) a nakonec jméno, aby bylo pořadí
 * deterministické i bez zásahu admina. Hráči bez jediného náhozu padají na konec.
 */
fun rankByTotal(rows: List<TournamentResultRow>): List<TournamentResultRow> =
    rows.sortedWith(
        compareByDescending<TournamentResultRow> { it.hasAnyScore }
            .thenByDescending { it.total }
            .thenComparator { a, b -> compareDescendingScores(a, b) }
            .thenBy { it.entry.qualRank ?: Int.MAX_VALUE }
            .thenBy { it.entry.playerName.lowercase() }
    )

/** Complete qualification ties, separated by gender as in the public qualification tables. */
fun qualificationTies(rows: List<TournamentResultRow>): List<List<TournamentResultRow>> =
    rows.filter { it.hasAnyScore }
        .groupBy { Triple(it.entry.gender, it.total, it.descendingScores) }.values.filter { it.size > 1 }

fun unresolvedQualificationIds(rows: List<TournamentResultRow>): Set<String> =
    qualificationTies(rows).filter { tied ->
        val ranks = tied.map { it.entry.qualRank }
        ranks.any { it == null || it < 1 } || ranks.distinct().size != tied.size
    }.flatten().map { it.entry.id }.toSet()

/** Bonifikační pořadí (spec 17.6) — jinak stejná pravidla, jen se řadí podle součtu s bonusem. */
fun rankByBonusTotal(rows: List<TournamentResultRow>, bonusPct: Int): List<TournamentResultRow> =
    rows.sortedWith(
        compareByDescending<TournamentResultRow> { it.hasAnyScore }
            .thenByDescending { it.totalWithBonus(bonusPct) }
            .thenByDescending { it.total }
            .thenComparator { a, b -> compareDescendingScores(a, b) }
            .thenBy { it.entry.playerName.lowercase() }
    )

/** Porovná sestupně seřazené hry prvek po prvku (vyšší nejlepší hra = lepší). */
private fun compareDescendingScores(a: TournamentResultRow, b: TournamentResultRow): Int {
    val left = a.descendingScores
    val right = b.descendingScores
    for (i in 0 until maxOf(left.size, right.size)) {
        val cmp = (right.getOrNull(i) ?: -1).compareTo(left.getOrNull(i) ?: -1)
        if (cmp != 0) return cmp
    }
    return 0
}

/**
 * Tým hráče v sezóně turnaje, nebo „host" (spec 17.8). Host se nepozná příznakem — pozná se tím,
 * že hráč v té sezóně nemá soupisku, takže se ani nedostane do žebříčku jednotlivců.
 */
const val TOURNAMENT_GUEST_LABEL = "host"

fun teamOrGuestLabel(playerId: String, teamByPlayer: Map<String, String>): String =
    teamByPlayer[playerId] ?: TOURNAMENT_GUEST_LABEL

fun isGuest(playerId: String, teamByPlayer: Map<String, String>): Boolean = playerId !in teamByPlayer

/** Řádky rozdělené podle pohlaví — pavouk má od kvalifikace dvě samostatné tabulky (spec 17.5). */
fun splitByGender(rows: List<TournamentResultRow>): Pair<List<TournamentResultRow>, List<TournamentResultRow>> =
    rows.filter { it.entry.gender == "male" } to rows.filter { it.entry.gender == "female" }

/** Účastníci bez vyplněného pohlaví — u pavouka je nelze zařadit do žádné z tabulek. */
fun withoutGender(rows: List<TournamentResultRow>): List<TournamentResultRow> =
    rows.filter { it.entry.gender != "male" && it.entry.gender != "female" }
